/**
 * liveOverlayStore — the EPHEMERAL live layer of the banco (review room).
 *
 * Holds only volatile collaboration state: who is present and which fields are
 * locked (and by whom). It is NEVER persisted — durable values live in the run
 * itself (TanStack Query) and are written on approve. Fed by ``useBancoRoom``
 * from the WS events (``banco_presence`` / ``banco_lock`` / ``banco_unlock``).
 *
 * Convention mirrors ``activeActionStore``: normalized records + tiny action
 * setters + standalone/curried selectors read through ``useShallow``.
 */

import { create } from "zustand";

export interface PresenceUser {
  readonly user_id: string;
  readonly name: string;
  readonly initials: string;
  readonly color: string;
}

/** A held field-lock; ``field`` is the flat dotted path shared with the BE. */
export interface FieldLock {
  readonly field: string;
  readonly user_id: string;
  readonly name: string;
  readonly initials: string;
  readonly color: string;
}

interface LiveOverlayState {
  /** The run this overlay currently mirrors — guards against cross-run bleed. */
  readonly runId: string | null;
  readonly presence: readonly PresenceUser[];
  readonly locks: Readonly<Record<string, FieldLock>>;

  /** Point the overlay at a run (clears prior state when it changes). */
  readonly bind: (runId: string) => void;
  /** Replace the full presence snapshot (server is authoritative). */
  readonly setPresence: (users: readonly PresenceUser[]) => void;
  readonly setLock: (lock: FieldLock) => void;
  readonly clearLock: (field: string) => void;
  /** Full teardown (leaving the room). */
  readonly reset: () => void;
}

const EMPTY_PRESENCE: readonly PresenceUser[] = Object.freeze([]);

export const useLiveOverlayStore = create<LiveOverlayState>((set) => ({
  runId: null,
  presence: EMPTY_PRESENCE,
  locks: {},

  bind: (runId) =>
    set((s) => (s.runId === runId ? s : { runId, presence: EMPTY_PRESENCE, locks: {} })),
  setPresence: (users) => set({ presence: users }),
  setLock: (lock) => set((s) => ({ locks: { ...s.locks, [lock.field]: lock } })),
  clearLock: (field) =>
    set((s) => {
      if (!(field in s.locks)) return s;
      const next = { ...s.locks };
      delete next[field];
      return { locks: next };
    }),
  reset: () => set({ runId: null, presence: EMPTY_PRESENCE, locks: {} }),
}));

// ── selectors ────────────────────────────────────────────────────────────────
export const selectPresence = (s: LiveOverlayState): readonly PresenceUser[] => s.presence;
export const selectLocks = (s: LiveOverlayState): Readonly<Record<string, FieldLock>> => s.locks;

/** Curried: the lock on ONE field (or undefined). Stable identity per field. */
export const selectLockFor =
  (field: string) =>
  (s: LiveOverlayState): FieldLock | undefined =>
    s.locks[field];
